import { useScannerContext } from 'contexts/ScannerContext';
import { useWarehouseWorkstationContext } from 'contexts/WarehouseWorkstationContext';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';
import useSnackbar from 'hooks/useSnackbar';
import { FC, useContext, useMemo, useState, createContext } from 'react';
import {
  useLazyGetHandlingRequestsV2Query,
  usePickedHandlingRequestsMutation,
} from 'store/slices/clientV2/handlingRequests';
import { Lite, ALL, QueryFilterOperators, NullOperatorValues } from 'types/api';
import { HandlingRequestSchema } from 'types/clientV2/stock';
import {
  StockLocationSchemaLite,
  StockLocationPurpose,
} from 'types/clientV2/stockLocations';
import { StockLocationType, StockLotCRUDSchema } from 'types/inventory';
import { FixedPickingStockLot } from 'views/warehouse/FixedPicking/types';
import { getHighestPriorityRequest } from 'views/warehouse/FixedPicking/utils';
import { WarehouseKitRequest } from 'views/warehouse/types';
import { useGetWarehouseKitRequestData } from 'views/warehouse/useGetWarehouseKitRequestData';
import { useMoveWarningContext } from 'views/warehouse/MoveWarningContext';

type FixedPickingContextType = {
  kitRequests: WarehouseKitRequest[];
  isLoadingKitRequests: boolean;
  stockLotsToPick: FixedPickingStockLot[];
  pickFromLocation: StockLocationSchemaLite | null;
  setPickFromLocation: (location: StockLocationSchemaLite) => void;
  selectedStockLot: FixedPickingStockLot | null;
  setSelectedStockLot: (stockLot: FixedPickingStockLot) => void;
  targetLocation: StockLocationSchemaLite | null;
  isLoadingStockLotsToPick: boolean;
  pickToBins: StockLocationSchemaLite[];
  refetchHandlingRequests: () => void;
  refetchKitRequests: () => void;
};

export const FixedPickingContext = createContext<
  FixedPickingContextType | undefined
>(undefined);

export const FixedPickingProvider: FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { validateMoveAndShowWarning } = useMoveWarningContext();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const { kitRequests, isLoadingKitRequests, refetchKitRequests } =
    useGetWarehouseKitRequestData({
      fetchKitRequestLines: false,
    });

  const { filteredWorkstationLocations: workstationLocations, targetLocation } =
    useWarehouseWorkstationContext();
  const [pickFromLocation, setPickFromLocation] =
    useState<StockLocationSchemaLite | null>(null);

  const [selectedStockLot, setSelectedStockLot] =
    useState<FixedPickingStockLot | null>(null);

  const [
    getLazyGetHandlingRequests,
    { data: handlingRequestsResponse, isLoading: isLoadingHandlingRequests },
  ] = useServerSideLazyQuery<
    HandlingRequestSchema,
    Lite<HandlingRequestSchema, 'stockLot'>
  >(useLazyGetHandlingRequestsV2Query);

  const stockLotsToPick: FixedPickingStockLot[] = useMemo(() => {
    const handlingRequests = handlingRequestsResponse?.data || [];
    const stockLotMap = handlingRequests.reduce<
      Record<string, FixedPickingStockLot>
    >((acc, handlingRequest) => {
      const stockLotId = handlingRequest.stockLot.id;

      if (!acc[stockLotId]) {
        acc[stockLotId] = {
          ...handlingRequest.stockLot,
          handlingRequest: null,
        };
      }

      const currentRequest = acc[stockLotId].handlingRequest;
      const newRequest = {
        ...handlingRequest,
        stockLot: stockLotId,
      };

      if (
        !currentRequest ||
        (currentRequest &&
          getHighestPriorityRequest([currentRequest, newRequest]) ===
            newRequest)
      ) {
        acc[stockLotId].handlingRequest = newRequest;
      }

      return acc;
    }, {});

    return Object.values(stockLotMap);
  }, [handlingRequestsResponse?.data]);

  const [
    pickedHandlingRequests,
    { isLoading: isPickingHandlingRequestsLoading },
  ] = usePickedHandlingRequestsMutation();

  const pickToBins = useMemo(
    () =>
      workstationLocations.filter(
        (location) =>
          location.locationType === StockLocationType.ESD_BIN &&
          location.purpose !== StockLocationPurpose.STORAGE
      ),
    [workstationLocations]
  );

  const fetchHandlingRequestsForBin = (binId: string) => {
    getLazyGetHandlingRequests({
      schema: [ALL, { stockLot: [] }],
      filters: [
        {
          // @ts-ignore NOTE: typescript handle this trickshot yet
          field: 'stockLot.stockLocation.parent',
          operator: QueryFilterOperators['='],
          value: binId,
        },
        {
          field: 'pickedAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
        {
          field: 'approvedAt',
          operator: QueryFilterOperators.isNotEmpty,
          value: NullOperatorValues.isNotEmpty,
        },
        {
          // eslint-disable-next-line no-restricted-syntax
          field: 'cancelledAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
        {
          field: 'completedAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
        {
          field: 'stockLot.deletedAt',
          operator: QueryFilterOperators.isEmpty,
          value: NullOperatorValues.isEmpty,
        },
      ],
    });
  };

  const pickStockLot = async (
    stockLot: FixedPickingStockLot,
    pickToLocation: StockLocationSchemaLite
  ) => {
    if (!stockLot.handlingRequest) {
      dispatchErrorSnackbar('No handling request found for stock lot');
      return;
    }
    try {
      await pickedHandlingRequests({
        ids: [stockLot.handlingRequest.id],
        stockLocation: pickToLocation.id,
      }).unwrap();
      validateMoveAndShowWarning(
        stockLot,
        pickToLocation,
        stockLot.handlingRequest
      );
      setSelectedStockLot(null);
      dispatchSuccessSnackbar('Stock lot picked for handling request');
    } catch (error) {
      dispatchErrorSnackbar('Error picking stock lot');
    }
  };

  const handleSetScannedBin = (scannedBin: StockLocationSchemaLite) => {
    setPickFromLocation(scannedBin);
    fetchHandlingRequestsForBin(scannedBin.id);
    setSelectedStockLot(null);
  };

  const handleSetScannedBinSection = (
    scannedBinSection: StockLocationSchemaLite
  ) => {
    if (selectedStockLot) {
      pickStockLot(selectedStockLot, scannedBinSection);
    }
  };

  const handleScannedStockLocation = (
    scannedStockLocation: StockLocationSchemaLite
  ) => {
    if (scannedStockLocation.locationType === StockLocationType.ESD_BIN)
      handleSetScannedBin(scannedStockLocation);
    if (scannedStockLocation.locationType === StockLocationType.BIN_SECTION)
      handleSetScannedBinSection(scannedStockLocation);
  };

  const handleScannedStockLot = (scannedStockLot: StockLotCRUDSchema) => {
    const matchedStockLot =
      stockLotsToPick.find((stockLot) => stockLot.id === scannedStockLot.id) ||
      null;
    if (!targetLocation) {
      setSelectedStockLot(matchedStockLot);
    }
    if (targetLocation && matchedStockLot)
      pickStockLot(matchedStockLot, targetLocation);
  };

  useScannerContext({
    handleScannedStockLocation,
    handleScannedStockLot,
  });

  const refetchHandlingRequests = () => {
    if (pickFromLocation) {
      fetchHandlingRequestsForBin(pickFromLocation.id);
    }
  };

  return (
    <FixedPickingContext.Provider
      value={{
        stockLotsToPick,
        kitRequests,
        isLoadingKitRequests,
        pickFromLocation,
        setPickFromLocation,
        selectedStockLot,
        setSelectedStockLot,
        targetLocation,
        isLoadingStockLotsToPick: isLoadingHandlingRequests,
        pickToBins,
        refetchHandlingRequests,
        refetchKitRequests,
      }}
    >
      {children}
    </FixedPickingContext.Provider>
  );
};

export const useFixedPickingContext = () => {
  const context = useContext(FixedPickingContext);
  if (!context)
    throw new Error(
      'useFixedPickingContext must be used within a FixedPickingProvider'
    );
  return context;
};
